import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';

/**
 * Bridge for "refresh the dashboard" requests raised by UI that lives OUTSIDE
 * the dashboard page — today the breadcrumb bar's Refresh button (the freshness
 * band's retired "Refresh Now" action, relocated to the breadcrumb line
 * 2026-08-06). AppComponent forwards the button's click to
 * {@link requestRefresh}; the mounted `DashboardPageComponent` subscribes
 * to {@link refreshRequest$} for the life of its mount and delegates to its
 * `onRefresh()` (cache invalidate + full re-fetch of the active view).
 *
 * A service of its own — NOT part of `DashboardDataStore` — so the data cache
 * keeps its one job and this cross-component event seam can change without
 * touching it (CodeAnt PR #227, agent-persona-maintainability).
 *
 * A plain `Subject`, deliberately not a `ReplaySubject`: a request fired while
 * no dashboard is mounted has nothing to act on and must NOT replay into the
 * next mount (whose own `ngOnInit` already fetches fresh enough) — same
 * no-replay reasoning as `AppStateResetService.sessionEnd`. The breadcrumb
 * bar's Refresh button only renders while a dashboard view is live, so in
 * practice there is always a listener.
 */
@Injectable({ providedIn: 'root' })
export class DashboardRefreshService {
  /** Backing subject — private so `requestRefresh()` stays the only emitter. */
  private readonly refreshRequests = new Subject<void>();

  /** Emits once per {@link requestRefresh}. */
  readonly refreshRequest$: Observable<void> = this.refreshRequests.asObservable();

  /** Asks the currently mounted dashboard (if any) to re-fetch everything,
      bypassing the `DashboardDataStore` cache — AppComponent calls this when
      the breadcrumb bar's Refresh button is clicked. */
  requestRefresh(): void {
    this.refreshRequests.next();
  }
}
